import {
  Employee,
  GetABankAccountResponse,
  GetANetPaySplitResponse,
  GetAnEarningRateResponse,
  GetAnEmployeesPaystubsResponse,
  GetCompanyDefinedAttributesResponse,
  ListEarningRatesResponse,
  ListEmployeesResponse,
  ListNetPaySplitsResponseItem,
  ListPayrollResponse,
  ListPayrollsRequestStatus,
  UpdateCompanyDefinedAttributesResponse,
} from '@thera-hr/check-api-sdk/api/types';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import {
  CHECK_DOUBLE_OVERTIME_EARNING_RATE_NAME,
  CHECK_EARNING_RATE_NAME,
  CHECK_OVERTIME_EARNING_RATE_NAME,
  globalFetcherSettings,
} from 'lib/constants';
import { IChecKPaystubWithStatus } from 'types/check';
import { getCheckEmployeeStatus } from 'utils/app';
import { useCheckFernClient } from './useFern';
import { extractCursorFromUrl } from 'utils/string';
import { UpdateCompanyDefinedAttributesRequest } from '@thera-hr/check-api-sdk/api/client/requests';
import { CHECK_500_PAYSTUB_ERROR_COMPANY } from 'lib/whitelistConstants';

const refreshOptions = {
  ...globalFetcherSettings,
  refreshInterval: 1000 * 60 * 10,
};

/**
 * This hook is used to get all employees for a company
 * @param checkCompanyId - The ID of the company to get employees for
 * @param isOrgAdminOrHRSpecialist - Whether the user is an org admin or HR specialist, only this type of user can see all employees
 * @param activeOnly - Whether to return only active employees (default: false)
 * @returns {checkEmployees: Employee[], mutateCheckEmployee: (employee: Employee) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckEmployeesSWR = ({
  checkCompanyId,
  isOrgAdminOrHRSpecialist,
  activeOnly = false,
}: {
  checkCompanyId: string;
  isOrgAdminOrHRSpecialist?: boolean;
  activeOnly?: boolean;
}) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getCheckEmployeesFetcher = async (): Promise<ListEmployeesResponse> => {
    const allEmployees: Employee[] = [];
    let cursor: string | undefined;

    do {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await checkFernApi.listEmployees({ company: checkCompanyId, limit: 100, cursor } as any);
      allEmployees.push(...(response.results ?? []));
      cursor = response.next ? extractCursorFromUrl(response.next) : undefined;
    } while (cursor);

    return { results: allEmployees };
  };

  const shouldCheckForOrgAdmin =
    isOrgAdminOrHRSpecialist === undefined || isOrgAdminOrHRSpecialist === null ? true : isOrgAdminOrHRSpecialist;
  const { data, error, mutate, isLoading } = useSWR(
    () =>
      shouldCheckForOrgAdmin && checkCompanyId && !isCheckFernLoading
        ? ['getCheckEmployeesFetcher', checkCompanyId]
        : false,
    getCheckEmployeesFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  // Filter employees based on activeOnly prop
  const filteredEmployees = useMemo(() => {
    const employees = data?.results || [];

    if (!activeOnly) {
      return employees;
    }

    return employees
      .map((employee) => ({
        ...employee,
        status: getCheckEmployeeStatus(employee),
      }))
      .filter((employee) => employee.status.text === 'Active');
  }, [data?.results, activeOnly]);

  return {
    checkEmployees: filteredEmployees,
    mutateCheckEmployee: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get an employee for a company
 * @param checkEmployeeId - The ID of the employee to get
 * @returns {checkEmployee: Employee, mutateCheckEmployee: (employee: Employee) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckEmployeeSWR = ({ checkEmployeeId }: { checkEmployeeId: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getCheckEmployeeFetcher = async (): Promise<Employee> => {
    const response = await checkFernApi.getAnEmployee(checkEmployeeId);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (checkEmployeeId && !isCheckFernLoading ? ['getCheckEmployeeFetcher', checkEmployeeId] : false),
    getCheckEmployeeFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    checkEmployee: data,
    mutateCheckEmployee: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get and update an employee's company-defined attributes
 * @param employeeID - The ID of the employee to get/update company-defined attributes for
 * @returns {companyDefinedAttributesResponse, updateCompanyDefinedAttributes, mutateCompanyDefinedAttributes, isPending, error}
 */
export const useCheckCompanyDefinedAttributesSWR = ({ employeeID }: { employeeID?: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getCompanyDefinedAttributesFetcher = async (): Promise<GetCompanyDefinedAttributesResponse> => {
    // SWR key ensures employeeID is present, but we still guard for type safety.
    if (!employeeID) {
      throw new Error('employeeID is required to fetch company-defined attributes.');
    }
    const response = await checkFernApi.getCompanyDefinedAttributes(employeeID);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (employeeID && !isCheckFernLoading ? ['getCompanyDefinedAttributesFetcher', employeeID] : false),
    getCompanyDefinedAttributesFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  const updateCompanyDefinedAttributes = async (
    request?: UpdateCompanyDefinedAttributesRequest,
  ): Promise<UpdateCompanyDefinedAttributesResponse> => {
    if (!employeeID) {
      throw new Error('employeeID is required to update company-defined attributes.');
    }

    const response = await checkFernApi.updateCompanyDefinedAttributes(employeeID, request);
    // Revalidate after updating so UI always reflects the persisted values.
    await mutate();
    return response;
  };

  return {
    companyDefinedAttributesResponse: data,
    companyDefinedAttributes: data?.companyDefinedAttributes || [],
    /**
     * Accepts the exact SDK request shape:
     * `{ companyDefinedAttributes: [{ name, value, effectiveStart? }] }`
     */
    updateCompanyDefinedAttributes,
    mutateCompanyDefinedAttributes: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get a bank account by ID
 * @param bankAccountID - The ID of the bank account to get
 * @returns {checkBankAccount: GetABankAccountResponse, mutateCheckBankAccount: (bankAccount: GetABankAccountResponse) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckBankAccountSWR = ({ bankAccountID }: { bankAccountID: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getABankAccountFetcher = async (): Promise<GetABankAccountResponse> => {
    const response = await checkFernApi.getABankAccount(bankAccountID);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (bankAccountID && !isCheckFernLoading ? ['getABankAccountFetcher', bankAccountID] : false),
    getABankAccountFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    checkBankAccount: data,
    mutateCheckBankAccount: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get all earning rates for an employee
 * @param employeeID - The ID of the employee to get earning rates for
 * @param active - Whether the earning rates are active
 * @returns {checkEarningRates: ListEarningRatesResponse, mutateCheckEarningRate: (earningRates: ListEarningRatesResponse) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckAllEarningRates = ({ employeeID, active = true }: { employeeID: string; active?: boolean }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getAllEarningRatesFetcher = async (): Promise<ListEarningRatesResponse> => {
    const response = await checkFernApi.listEarningRates({ employee: employeeID, ...(active && { active }) });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (employeeID && !isCheckFernLoading ? ['getAllEarningRatesFetcher', employeeID] : false),
    getAllEarningRatesFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  const checkEarningRate = data?.results?.find((earningRate) => earningRate.name === CHECK_EARNING_RATE_NAME);
  const defaultEarningRate = checkEarningRate ? checkEarningRate : data?.results?.[0];

  // return all active earning rates except the default, overtime, and double overtime rates
  const otherActiveEarningRates = data?.results?.filter(
    (earningRate) =>
      earningRate.active &&
      earningRate.name !== CHECK_EARNING_RATE_NAME &&
      earningRate.name !== CHECK_OVERTIME_EARNING_RATE_NAME &&
      earningRate.name !== CHECK_DOUBLE_OVERTIME_EARNING_RATE_NAME &&
      earningRate.id !== defaultEarningRate?.id, // Exclude the defaultEarningRate to prevent duplication
  );

  // return all active earning rates except the overtime and double overtime rates
  const checkEarningRates = data?.results?.filter(
    (earningRate) =>
      earningRate.active &&
      earningRate.name !== CHECK_OVERTIME_EARNING_RATE_NAME &&
      earningRate.name !== CHECK_DOUBLE_OVERTIME_EARNING_RATE_NAME,
  );

  return {
    checkEarningRates,
    defaultEarningRate,
    otherActiveEarningRates,
    mutateCheckEarningRate: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get active earning rates for multiple employees in batch
 * @param employeeIDs - Array of employee IDs to get earning rates for
 * @param active - Whether the earning rates are active (default: true)
 * @returns {earningRatesMap: Record<string, boolean>, periodByEmployeeId: Record<string, string | undefined>, ratesByEmployeeId: Record<string, GetAnEarningRateResponse[]>, isPending: boolean, error: Error}
 *          earningRatesMap maps employee ID to whether they have at least one active earning rate.
 *          periodByEmployeeId maps employee ID to the period of their default (regular_compensation,
 *          else first) earning rate — 'hourly' vs 'annually' — the same signal the rest of the app
 *          uses to tell hourly from salaried employees.
 *          ratesByEmployeeId maps employee ID to all of their fetched earning rates.
 */
export const useCheckAllEarningRatesBatch = ({
  employeeIDs,
  active = true,
}: {
  employeeIDs: string[];
  active?: boolean;
}) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getEarningRatesBatchFetcher = async (): Promise<
    Record<string, { hasActiveRate: boolean; period?: string; rates: GetAnEarningRateResponse[] }>
  > => {
    if (!checkFernApi || employeeIDs.length === 0) return {};
    const ratesByEmployeeId: Record<
      string,
      { hasActiveRate: boolean; period?: string; rates: GetAnEarningRateResponse[] }
    > = {};
    employeeIDs.forEach((employeeId) => {
      ratesByEmployeeId[employeeId] = { hasActiveRate: false, period: undefined, rates: [] };
    });
    // The earning_rates endpoint only honors a single employee per request (repeated or
    // comma-separated employee params silently return wrong results), so fetch per employee
    // but throttled in small batches: firing all requests at once trips Check's ~25 req/s
    // limit (429s) for companies with dozens of employees. Same throttle as _admin/check-employees.
    const BATCH_SIZE = 5;
    const BATCH_DELAY_MS = 250;
    for (let i = 0; i < employeeIDs.length; i += BATCH_SIZE) {
      const batch = employeeIDs.slice(i, i + BATCH_SIZE);
      await Promise.all(
        batch.map(async (employeeId) => {
          try {
            const response = await checkFernApi.listEarningRates({ employee: employeeId, ...(active && { active }) });
            const results = response.results ?? [];
            // Mirror useCheckAllEarningRates' default-rate selection (and the backend's hourly filter in
            // W2PayrollService.getHourlyCheckEmployeeIds): the rate named regular_compensation, else the first.
            const defaultRate = results.find((rate) => rate.name === CHECK_EARNING_RATE_NAME) ?? results[0];
            ratesByEmployeeId[employeeId] = {
              hasActiveRate: results.length > 0,
              period: defaultRate?.period,
              rates: results,
            };
          } catch (error) {
            console.error(`Failed to fetch earning rates for employee ${employeeId}:`, error);
          }
        }),
      );
      if (i + BATCH_SIZE < employeeIDs.length) {
        await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
      }
    }
    return ratesByEmployeeId;
  };

  const { data, error, isLoading } = useSWR(
    () =>
      employeeIDs.length > 0 && !isCheckFernLoading
        ? ['getEarningRatesBatchFetcher', [...employeeIDs].sort().join(',')]
        : null,
    getEarningRatesBatchFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      ...refreshOptions,
    },
  );

  // Preserve the original boolean contract for existing callers, and expose the default-rate period.
  const earningRatesMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    Object.entries(data ?? {}).forEach(([employeeId, rate]) => {
      map[employeeId] = rate.hasActiveRate;
    });
    return map;
  }, [data]);

  const periodByEmployeeId = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    Object.entries(data ?? {}).forEach(([employeeId, rate]) => {
      map[employeeId] = rate.period;
    });
    return map;
  }, [data]);

  const ratesByEmployeeId = useMemo(() => {
    const map: Record<string, GetAnEarningRateResponse[]> = {};
    Object.entries(data ?? {}).forEach(([employeeId, rate]) => {
      map[employeeId] = rate.rates ?? [];
    });
    return map;
  }, [data]);

  return {
    earningRatesMap,
    periodByEmployeeId,
    ratesByEmployeeId,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get an earning rate by ID
 * @param earningRateID - The ID of the earning rate to get
 * @returns {checkEarningRate: GetAnEarningRateResponse, mutateCheckEarningRate: (earningRate: GetAnEarningRateResponse) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckEarningRate = ({ earningRateID }: { earningRateID: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getEarningRateFetcher = async (): Promise<GetAnEarningRateResponse> => {
    const response = await checkFernApi.getAnEarningRate(earningRateID);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (earningRateID && !isCheckFernLoading ? ['getEarningRateFetcher', earningRateID] : false),
    getEarningRateFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    checkEarningRate: data,
    mutateCheckEarningRate: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get all paystubs for an employee
 * @param employeeID - The ID of the employee to get paystubs for
 * @returns {checkPaystubs: IChecKPaystubWithStatus[], mutateCheckPaystubs: (paystubs: IChecKPaystubWithStatus[]) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckPaystubs = ({
  employeeID,
  yearToDate = true,
  populatePayrollStatus = false,
  status,
}: {
  employeeID: string;
  yearToDate?: boolean;
  populatePayrollStatus?: boolean;
  status?: 'paid' | 'pending' | 'processing' | 'failed';
}) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();
  const [paystubsWithStatus, setPaystubsWithStatus] = useState<{
    payload: IChecKPaystubWithStatus[];
    isLoading: boolean;
  }>({
    payload: undefined,
    isLoading: true,
  });

  const startDate = yearToDate ? new Date().getFullYear() + '-01-01' : undefined;
  const statusParam = status ? status : undefined;

  const getPaystubsFetcher = async (): Promise<GetAnEmployeesPaystubsResponse> => {
    const response = await checkFernApi.getAnEmployeesPaystubs(employeeID, {
      start: startDate,
      status: statusParam,
    });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (employeeID && !isCheckFernLoading ? ['getPaystubsFetcher', employeeID, startDate, statusParam] : false),
    getPaystubsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  useEffect(() => {
    // Add status to all the paystubs
    if (data?.results?.length && populatePayrollStatus) {
      setPaystubsWithStatus((prevState) => ({ ...prevState, isLoading: true }));

      (async () => {
        const paystubsWithStatusPromises = data?.results?.map((data) => {
          return checkFernApi.getPayroll(data.payroll);
        });

        await Promise.allSettled(paystubsWithStatusPromises).then((res) => {
          const _paystubs = data?.results?.map((paystub, index) => {
            if (res[index].status === 'fulfilled') {
              const payrollItems = (res[index] as PromiseFulfilledResult<any>)?.value.items;
              const matchedPayrollItem = payrollItems.find(
                (payrollItem) => payrollItem.employee === employeeID && payrollItem.payroll === paystub.payroll,
              );
              return { ...paystub, status: matchedPayrollItem.status };
            } else {
              return { ...paystub, status: '' };
            }
          });

          setPaystubsWithStatus({ payload: _paystubs, isLoading: false });
        });
      })();
    } else {
      setPaystubsWithStatus({ payload: data?.results, isLoading: false });
    }
    // checkFernApi will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.results, employeeID, populatePayrollStatus]);

  return {
    checkPaystubs: paystubsWithStatus?.payload,
    mutateCheckPaystubs: mutate,
    isPending: isLoading || paystubsWithStatus.isLoading,
    error,
  };
};

/**
 * Workaround hook for companies where the Check paystubs API returns 500.
 * Uses listPayrolls (company-wide) filtered by employee, then maps to IChecKPaystubWithStatus format.
 * For non-whitelisted companies, falls back to the regular useCheckPaystubs hook.
 */
export const useCheckPaystubsWithFallback = ({
  theraCompanyId,
  checkCompanyId,
  employeeID,
  yearToDate = false,
  populatePayrollStatus = false,
}: {
  theraCompanyId?: string;
  checkCompanyId?: string;
  employeeID?: string;
  yearToDate?: boolean;
  populatePayrollStatus?: boolean;
}) => {
  // no-dd-sa:typescript-inclusive/declarations
  const isWhitelisted = CHECK_500_PAYSTUB_ERROR_COMPANY.some((company) => company.theraCompanyId === theraCompanyId);

  // Regular path for non-whitelisted companies
  // thera-override: all
  const regularResult = useCheckPaystubs({
    employeeID: (!isWhitelisted ? employeeID : undefined) as string,
    yearToDate,
    populatePayrollStatus,
  });

  // Fallback path: use company-wide listPayrolls for whitelisted companies
  const {
    checkPayrolls,
    isPending: isPayrollsPending,
    error: payrollsError,
  } = useCheckPayrollSWR({
    companyID: (isWhitelisted ? checkCompanyId : undefined) as string,
    status: 'paid',
  });

  const payrollResults = checkPayrolls?.results;

  const fallbackPaystubs = useMemo<IChecKPaystubWithStatus[]>(() => {
    if (!isWhitelisted || !payrollResults?.length || !employeeID) return undefined;

    return payrollResults
      .filter((payroll) => payroll.items?.some((item) => item.employee === employeeID))
      .map((payroll) => {
        const employeeItem = payroll.items?.find((item) => item.employee === employeeID);
        const grossPay = employeeItem?.earnings?.reduce((sum, e) => sum + Number(e.amount || 0), 0) || 0;
        const netPay = Number(employeeItem?.netPay || 0);
        const employeeTaxes =
          employeeItem?.taxes
            ?.filter((t) => (t as any).payer === 'employee' || !(t as any).payer)
            ?.reduce((sum, t) => sum + Number(t.amount || 0), 0) || 0;

        return {
          payroll: payroll.id,
          payday: payroll.payday,
          periodStart: payroll.periodStart,
          periodEnd: payroll.periodEnd,
          paymentMethod: employeeItem?.paymentMethod,
          earnings: employeeItem?.earnings?.map((e) => ({
            type: e.type,
            description: e.description,
            hours: e.hours,
            amount: e.amount,
          })),
          summary: {
            earnings: String(grossPay),
            netPay: String(netPay),
            employeeTaxes: String(employeeTaxes),
            earningsYtd: undefined,
            netPayYtd: undefined,
            employeeTaxesYtd: undefined,
          },
          status: payroll.status,
        } as IChecKPaystubWithStatus;
      });
  }, [isWhitelisted, payrollResults, employeeID]);

  if (isWhitelisted) {
    return {
      checkPaystubs: fallbackPaystubs,
      mutateCheckPaystubs: undefined,
      isPending: isPayrollsPending,
      error: payrollsError,
    };
  }

  return regularResult;
};

/**
 * This hook is used to get all payrolls for a company, maximum limit is 25 per page, use cursor to get the next page
 * @param companyID - The ID of the company to get payrolls for
 * @param status - The status of the payrolls to get
 * @param paydayAfter - Return payrolls whose payday is on or after the provided date (format: YYYY-MM-DD)
 * @returns {checkPayrolls: ListPayrollResponse, mutateCheckPayrolls: (payrolls: ListPayrollResponse) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckPayrollSWR = ({
  companyID,
  status,
  paydayAfter,
}: {
  companyID: string;
  status?: ListPayrollsRequestStatus;
  paydayAfter?: string; // Return payrolls whose payday is on or after the provided date (format: YYYY-MM-DD)
}) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getPayrollsFetcher = async (): Promise<ListPayrollResponse> => {
    const response = await checkFernApi.listPayrolls({
      company: companyID,
      status: status,
      paydayAfter: paydayAfter,
    });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyID && !isCheckFernLoading ? ['getPayrollsFetcher', companyID, status, paydayAfter] : false),
    getPayrollsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    checkPayrolls: data,
    mutateCheckPayrolls: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get multiple bank accounts by their IDs
 * @param bankAccountIDs - Array of bank account IDs to fetch
 * @returns {bankAccounts: GetABankAccountResponse[], mutateBankAccounts, isPending, error}
 */
export const useCheckMultipleBankAccountsSWR = ({ bankAccountIDs }: { bankAccountIDs: string[] }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const validIDs = useMemo(() => (bankAccountIDs || []).filter(Boolean), [bankAccountIDs]);

  const getBankAccountsFetcher = async (): Promise<GetABankAccountResponse[]> => {
    const results = await Promise.all(
      validIDs.map(async (id) => {
        try {
          return await checkFernApi.getABankAccount(id);
        } catch {
          return null;
        }
      }),
    );
    return results.filter(Boolean) as GetABankAccountResponse[];
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (validIDs.length > 0 && !isCheckFernLoading ? ['getMultipleBankAccountsFetcher', ...validIDs.sort()] : false),
    getBankAccountsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    bankAccounts: data || [],
    mutateBankAccounts: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook fetches the default net pay split for an employee
 * @param netPaySplitId - The ID of the net pay split (from employee.defaultNetPaySplit)
 * @returns {netPaySplit, mutateNetPaySplit, isPending, error}
 */
export const useCheckNetPaySplitSWR = ({ netPaySplitId }: { netPaySplitId?: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getNetPaySplitFetcher = async (): Promise<GetANetPaySplitResponse> => {
    return await checkFernApi.getANetPaySplit(netPaySplitId);
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (netPaySplitId && !isCheckFernLoading ? ['getNetPaySplitFetcher', netPaySplitId] : false),
    getNetPaySplitFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    netPaySplit: data,
    mutateNetPaySplit: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook lists all net pay splits for an employee
 * @param employeeId - The Check employee ID
 * @returns {netPaySplits, mutateNetPaySplits, isPending, error}
 */
export const useCheckNetPaySplitListSWR = ({ employeeId }: { employeeId?: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const listNetPaySplitsFetcher = async (): Promise<ListNetPaySplitsResponseItem[]> => {
    const response = await checkFernApi.listNetPaySplits({ employee: employeeId });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (employeeId && !isCheckFernLoading ? ['listNetPaySplitsFetcher', employeeId] : false),
    listNetPaySplitsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    netPaySplits: Array.isArray(data) ? data : [],
    mutateNetPaySplits: mutate,
    isPending: isLoading,
    error,
  };
};
